<template>
  <FocusTrap :active="trapActive" :allow-outside-click="false" :initial-focus="() => $refs.initialFocusElement">
    <div class="app-modal-wrapper">
      <!-- Modal Backdrop -->
      <Transition name="modal-backdrop" @after-enter="onAfterEnterBackdrop" @after-leave="onAfterLeaveBackdrop">
        <div
          v-if="isClient && show"
          class="app-modal-backdrop"
          :class="{ 'app-modal-backdrop-static': modalStatic, 'app-modal-backdrop-transparent': transparentBackdrop }"
          :style="{ zIndex: zIndexes[0] }"
          @click="handleBackdropClick"
        />
      </Transition>

      <!-- Modal Dialog -->
      <Transition name="modal" @after-enter="onAfterEnterModal" @after-leave="onAfterLeaveModal">
        <div
          v-if="isClient && show"
          class="app-modal"
          :class="modalClasses"
          tabindex="-1"
          role="dialog"
          :aria-labelledby="titleId"
          :aria-describedby="bodyId"
          :style="{ zIndex: zIndexes[1] }"
          @click="handleModalClick"
          @keydown.esc="handleEscapeKey"
        >
          <div ref="initialFocusElement" class="app-modal-dialog" :class="dialogClasses" role="document" tabindex="-1">
            <div class="app-modal-content" :class="{ 'app-modal-content-borderless': !contentBorders }">
              <!-- Modal Header -->
              <div v-if="!hideHeader" class="app-modal-header">
                <slot name="header">
                  <h5
                    v-show="title"
                    :id="titleId"
                    class="app-modal-title"
                    :class="{ 'app-modal-title-centered': titleCentered }"
                  >
                    {{ title }}
                  </h5>
                  <button v-if="!hideCloseButton" aria-label="Close" class="btn-close" type="button" @click="hide">
                    <AppCrossIcon />
                  </button>
                </slot>
              </div>

              <!-- Modal Body -->
              <div :id="bodyId" class="app-modal-body">
                <slot name="default" :is-fully-displayed="isFullyDisplayed" :title-id="titleId" />
              </div>

              <!-- Modal Footer -->
              <div v-if="!hideFooter" class="app-modal-footer" :class="{ 'app-modal-footer-centered': footerCentered }">
                <slot name="footer">
                  <div class="footer-actions">
                    <button
                      v-if="actionClose"
                      type="button"
                      class="footer-action footer-action-cancel"
                      @click="handleCancel"
                    >
                      <slot name="footer-action-cancel">Cancel</slot>
                    </button>
                    <button
                      v-if="showSaveButton"
                      type="button"
                      class="footer-action footer-action-submit"
                      @click="handleSave"
                    >
                      <slot name="footer-action-submit">Submit</slot>
                    </button>
                  </div>
                </slot>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </FocusTrap>
</template>

<script>
import { FocusTrap } from 'focus-trap-vue';
import AppCrossIcon from '@/components/icons/TheCrossIcon.vue';
import uniqueId from '@/helpers/uniqueId';

export default {
  components: { FocusTrap, AppCrossIcon },
  props: {
    actionClose: {
      type: Boolean,
      default: false,
    },
    backdrop: {
      type: [Boolean, String],
      default: true,
      validator: (value) => [true, false, 'static'].includes(value),
    },
    baseZIndex: {
      type: Number,
      default: () => 1050,
      validator: (value) => value >= 1050,
    },
    centered: {
      type: Boolean,
      default: false,
    },
    contentBorders: {
      type: Boolean,
      default: false,
    },
    closeByEscape: {
      type: Boolean,
      default: true,
    },
    footerCentered: {
      type: Boolean,
      default: false,
    },
    fullscreen: {
      type: [Boolean, String],
      default: false,
      validator: (value) => [false, true, 'sm', 'md', 'lg', 'xl', 'xxl'].includes(value),
    },
    hideCloseButton: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: true,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    scrollable: {
      type: Boolean,
      default: false,
    },
    showSaveButton: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
      default: '',
      validator: (value) => ['', 'sm', 'lg', 'xl'].includes(value),
    },
    title: {
      type: String,
      default: '',
    },
    titleCentered: {
      type: Boolean,
      default: false,
    },
    transparentBackdrop: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isClient: false,
      isFullyDisplayedItems: {
        backdrop: false,
        modal: false,
      },
      show: false,
      staticBackdropTriggered: false,
      trapActive: false,
      uuid: uniqueId(),
    };
  },

  computed: {
    bodyId() {
      return `app-modal-body-${this.uuid}`;
    },
    titleId() {
      return `app-modal-title-${this.uuid}`;
    },
    dialogClasses() {
      const classes = {};

      if (this.transparentBackdrop) {
        classes['app-modal-dialog-transparent-backdrop'] = true;
      }

      // Size classes
      if (this.size) {
        classes[`app-modal-${this.size}`] = true;
      }

      // Centered
      if (this.centered) {
        classes['app-modal-dialog-centered'] = true;
      }

      // Scrollable
      if (this.scrollable) {
        classes['app-modal-dialog-scrollable'] = true;
      }

      // Fullscreen
      if (this.fullscreen === true) {
        classes['app-modal-fullscreen'] = true;
      } else if (this.fullscreen) {
        classes[`app-modal-fullscreen-${this.fullscreen}-down`] = true;
      }

      return classes;
    },
    modalClasses() {
      return {
        'app-modal-static-animation': this.staticBackdropTriggered,
      };
    },
    modalStatic() {
      return this.backdrop === 'static';
    },
    isFullyDisplayed() {
      return this.isFullyDisplayedItems.backdrop && this.isFullyDisplayedItems.modal;
    },
    zIndexes() {
      return [this.baseZIndex, this.baseZIndex + 5];
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.showModal();
          return;
        }
        this.hideModal();
      },
    },

    show: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.trapActive = false;
          return;
        }
        this.$nextTick(() => {
          this.trapActive = true;
        });
      },
    },

    isFullyDisplayed(newValue) {
      if (!newValue) {
        this.restoreBodyOverflow();
        this.$emit('hidden');
        return;
      }
      this.$emit('shown');
    },
  },

  mounted() {
    this.isClient = true;
  },

  beforeDestroy() {
    this.restoreBodyOverflow();
    document.removeEventListener('keydown', this.handleDocumentKeydown);
  },

  methods: {
    showModal() {
      this.show = true;
      this.setBodyOverflow();
      this.$emit('show');
      document.addEventListener('keydown', this.handleDocumentKeydown);
    },

    hide() {
      this.$emit('input', false);
      this.hideModal();
    },

    hideModal() {
      this.show = false;
      this.$emit('hide');
      document.removeEventListener('keydown', this.handleDocumentKeydown);
    },

    handleBackdropClick() {
      if (this.backdrop === true) {
        this.hide();
      } else if (this.modalStatic) {
        this.triggerStaticBackdrop();
      }
    },

    handleModalClick(e) {
      // Prevent backdrop click when clicking inside modal content
      if (e.target === e.currentTarget) {
        this.handleBackdropClick();
      }
    },

    handleEscapeKey() {
      if (this.closeByEscape && !this.modalStatic) {
        this.hide();
      }
    },

    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && this.show) {
        this.handleEscapeKey();
      }
    },

    handleCancel() {
      this.$emit('decline');
      this.hide();
    },

    handleSave() {
      this.$emit('save');
    },

    triggerStaticBackdrop() {
      this.staticBackdropTriggered = true;
      setTimeout(() => {
        this.staticBackdropTriggered = false;
      }, 300);
    },

    setBodyOverflow() {
      this.$store.dispatch('disableScroll');
    },

    restoreBodyOverflow() {
      this.$store.dispatch('enableScroll');
    },

    onAfterEnterBackdrop() {
      this.isFullyDisplayedItems.backdrop = true;
    },
    onAfterLeaveBackdrop() {
      this.isFullyDisplayedItems.backdrop = false;
    },

    onAfterEnterModal() {
      this.isFullyDisplayedItems.modal = true;
    },
    onAfterLeaveModal() {
      this.isFullyDisplayedItems.modal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.app-modal-wrapper {
  --app-modal-dialog-margin: 8px;
  --app-modal-content-padding-x: 24px;
  --app-modal-content-padding-x-half: calc(var(--app-modal-content-padding-x) / 2);
  --app-modal-content-padding-y: 16px;
  --app-modal-btn-close-size: 40px;

  /* Modal Base Styles */
  .app-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    outline: 0;

    /* Static Backdrop Animation */
    &.app-modal-static-animation {
      .app-modal-dialog {
        transform: scale(1.02);
      }
    }
  }

  .app-modal-dialog {
    position: relative;
    width: auto;
    margin: var(--app-modal-dialog-margin);
    pointer-events: none;
    transition: transform 0.3s ease-out;
    outline: 0;

    @media (min-width: vars.$mobile-break-point) {
      --app-modal-dialog-margin: 1.75rem;

      max-width: 500px;
      margin-left: auto;
      margin-right: auto;

      &.app-modal-sm {
        max-width: 300px;
      }
    }

    @media (min-width: vars.$desktop-lg-992-break-point) {
      &.app-modal-lg,
      &.app-modal-xl {
        max-width: 800px;
      }
    }

    @media (min-width: vars.$desktop-xl-1200-break-point) {
      &.app-modal-xl {
        max-width: 1140px;
      }
    }
  }

  .app-modal-content {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    pointer-events: auto;
    background-color: vars.$c-total-white;
    background-clip: padding-box;
    border: 1px solid rgba(vars.$c-total-black, 0.2);
    outline: 0;
    box-shadow: 0 0.125rem 0.25rem rgba(vars.$c-total-black, 0.075);
  }

  .app-modal-content-borderless {
    border: 0;

    .app-modal-header {
      border-bottom: 0;
    }
    .app-modal-footer {
      border-top: 0;
    }
  }

  /* Modal Header */
  .app-modal-header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: var(--app-modal-content-padding-y) var(--app-modal-content-padding-x) var(--app-modal-content-padding-y);
    border-bottom: 1px solid vars.$c-gray-da;
  }

  .app-modal-title {
    flex: 1;
    margin-bottom: 0;
    line-height: 1.2;
    font-size: 1.25rem;
    font-weight: 500;
    letter-spacing: 1px;
    text-transform: uppercase;

    &.app-modal-title-centered {
      padding-left: calc(var(--app-modal-btn-close-size) - var(--app-modal-content-padding-x-half));
      text-align: center;
    }
  }

  .btn-close {
    width: var(--app-modal-btn-close-size);
    height: var(--app-modal-btn-close-size);
    margin-left: auto;
    margin-right: calc(var(--app-modal-content-padding-x-half) * -1);
    cursor: pointer;

    &:hover {
      opacity: 0.75;
    }
  }

  /* Modal Body */
  .app-modal-body {
    position: relative;
    flex: 1 1 auto;
    padding: var(--app-modal-content-padding-y) var(--app-modal-content-padding-x) var(--app-modal-content-padding-x);
  }

  /* Modal Footer */
  .app-modal-footer {
    display: flex;
    flex-wrap: wrap;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
    padding: var(--app-modal-content-padding-y) var(--app-modal-content-padding-x)
      calc(var(--app-modal-content-padding-y) + 8px);
    border-top: 1px solid vars.$c-gray-da;

    & > * {
      margin: 0.25rem;
    }

    &-centered {
      justify-content: center;
    }
  }

  /* Footer Actions */
  .footer-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .footer-action {
    min-width: 90px;
    font-weight: 400;
    line-height: 1.5;
    text-align: center;
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    background-color: transparent;
    border: 1px solid transparent;
    padding: 8px 12px;
    font-size: 16px;
    letter-spacing: 1px;
    text-transform: uppercase;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;
  }

  .footer-action-submit {
    color: vars.$c-total-white;
    background-color: vars.$c-dark-gray;
    border-color: vars.$c-dark-gray;

    &:hover {
      background-color: lighten(vars.$c-dark-gray, 20%);
    }

    &:focus-visible {
      outline-color: vars.$c-gray-86;
    }
  }

  .footer-action-cancel {
    color: vars.$c-dark-gray;
    background-color: vars.$c-total-white;
    border-color: vars.$c-dark-gray;

    &:hover {
      background-color: vars.$c-light-f4;
    }
  }

  /* Backdrop */
  .app-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: vars.$c-total-black;
    opacity: 0.5;

    &.app-modal-backdrop-transparent {
      background-color: transparent;
    }
  }

  /* Centered Modal */
  .app-modal-dialog-centered {
    display: flex;
    align-items: center;
    min-height: calc(100% - var(--app-modal-dialog-margin) * 2);
  }

  .app-modal-dialog-transparent-backdrop .app-modal-content {
    border: 0;
    box-shadow: 8px 5px 70px 10px rgba(vars.$c-total-black, 0.14);
  }

  /* Scrollable Modal */
  .app-modal-dialog-scrollable {
    height: calc(100% - var(--app-modal-dialog-margin) * 2);

    .app-modal-content {
      max-height: 100%;
      overflow: hidden;
    }

    .app-modal-body {
      overflow-y: auto;
    }
  }

  /* Fullscreen Modals */
  .app-modal-fullscreen {
    width: 100vw;
    max-width: none;
    height: 100%;
    margin: 0;

    .app-modal-content {
      height: 100%;
      border: 0;
    }
  }

  /* Responsive Fullscreen */
  @media (max-width: vars.$mobile-break-point) {
    .app-modal-fullscreen-sm-down {
      width: 100vw;
      max-width: none;
      height: 100%;
      margin: 0;

      .app-modal-content {
        height: 100%;
        border: 0;
      }
    }
  }

  @media (max-width: vars.$tablet-break-point) {
    .app-modal-fullscreen-md-down {
      width: 100vw;
      max-width: none;
      height: 100%;
      margin: 0;

      .app-modal-content {
        height: 100%;
        border: 0;
      }
    }
  }

  @media (max-width: vars.$desktop-lg-992-break-point) {
    .app-modal-fullscreen-lg-down {
      width: 100vw;
      max-width: none;
      height: 100%;
      margin: 0;

      .app-modal-content {
        height: 100%;
        border: 0;
      }
    }
  }

  @media (max-width: vars.$desktop-xl-1200-break-point) {
    .app-modal-fullscreen-xl-down {
      width: 100vw;
      max-width: none;
      height: 100%;
      margin: 0;

      .app-modal-content {
        height: 100%;
        border: 0;
      }
    }
  }

  @media (max-width: vars.$desktop-xl-1400-break-point) {
    .app-modal-fullscreen-xxl-down {
      width: 100vw;
      max-width: none;
      height: 100%;
      margin: 0;

      .app-modal-content {
        height: 100%;
        border: 0;
      }
    }
  }
}

/* Transitions */
.modal {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.15s linear;
  }

  &-enter,
  &-leave-to {
    opacity: 0;

    .modal-dialog {
      transform: translate(0, -50px);
    }
  }
}

.modal-backdrop {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.15s linear;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }
}
</style>
